import React, { useState } from "react";
import { Container, Box, Typography, TextField, Button, Paper, CircularProgress, IconButton, Tooltip, Snackbar, Chip } from "@mui/material";
import axios from "axios";

const GEMINI_MODEL = "gemini-2.5-flash";

export default function App() {
  // Gemini key connection state
  const [apiKey, setApiKey] = useState("");
  const [isConnected, setIsConnected] = useState(false);

  // User input state
  const [phenomenon, setPhenomenon] = useState("");
  const [subject, setSubject] = useState("");
  const [affiliateLink, setAffiliateLink] = useState("");
  const [affiliateProductName, setAffiliateProductName] = useState("");
  const [blog, setBlog] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  // Step 1: Connect to Gemini
  function handleConnect() {
    if (apiKey.trim().length === 0) {
      setError("Please enter your Gemini API key.");
      return;
    }
    setIsConnected(true);
    setError("");
  }

  function handleDisconnect() {
    setIsConnected(false);
    setApiKey("");
    setBlog("");
    setError("");
  }

  // Step 2: Generate blog post
  async function handleSubmit() {
    setError("");
    setBlog("");
    const cleanPhenomenon = phenomenon.trim();
    const cleanSubject = subject.trim();
    if (!cleanPhenomenon || !cleanSubject) {
      setError("Please enter both a phenomenon/concept and its subject.");
      return;
    }
    setLoading(true);
    try {
      const cleanLink = affiliateLink.trim();
      const cleanProductName = affiliateProductName.trim();

      // LinkedIn-post contract: one hook title, then a single flowing
      // conversational narrative — no section headings, no markdown bold/bullets
      // (LinkedIn doesn't render Markdown, so **asterisks** would show up
      // literally in the post). Word count is capped so it reads as one
      // scrollable post instead of needing "see more" forever.
      const prompt = `You are writing a LinkedIn post for a science-education audience — smart but casual readers, not academics.
Topic: explain the physics/science behind "${cleanPhenomenon}" in the subject "${cleanSubject}".

TONE:
- Conversational and warm, like you're telling a friend a surprising story — use "you", contractions, and short punchy sentences mixed with longer ones.
- Simple English. No jargon, no technical terms without instantly explaining them in plain words.
- A rhetorical question or two is welcome, but don't overdo it.

CONTENT:
- Tell it as a real, engaging explanation with a clear hook, a turning point or "aha" moment, and a payoff — not a list of facts.
- If there's a genuine, well-known year or discovery tied to the underlying science, include it accurately. If the topic is a modern product/application with no single discovery date (e.g. furniture, gadgets), do NOT invent a fake year — just explain the real physics principle clearly instead.
- Ground it in one memorable specific detail (a real name, a place, a concrete physical mechanism) rather than vague generalities.
${cleanLink ? `- This post exists to lead into a product recommendation, so connect the physics explanation naturally to why it matters for "${cleanProductName || cleanPhenomenon}" specifically.
- End with exactly one short, natural call-to-action sentence (not salesy, no hashtags, no emojis) that references the product and includes the exact placeholder token {{LINK}} on its own — do not write out any URL yourself, just insert {{LINK}} where the link should go.`
: `- End with one short, punchy line that leaves the reader thinking — not a generic "let's keep exploring" wrap-up.`}

FORMAT — follow exactly:
- Line 1: a single "# " followed by a short, catchy, curiosity-hook title (like a headline, not a full sentence). Nothing else on that line.
- Line 2: blank.
- Then the post as plain flowing paragraphs. NO markdown at all in the body — no "##" headings, no "**bold**", no "-" or "*" bullet lists, no numbered lists. Just plain sentences and paragraph breaks.
- Total length: strictly between 150 and 220 words (not counting the title, but including the closing CTA line if present). This must fit comfortably as one LinkedIn post — do not exceed 220 words under any circumstance.`;

      const response = await axios.post(
        `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent`,
        {
          contents: [
            {
              parts: [{ text: prompt }]
            }
          ]
        },
        {
          headers: {
            "Content-Type": "application/json"
          },
          params: {
            key: apiKey.trim()
          }
        }
      );

      const blockReason = response.data?.promptFeedback?.blockReason;
      if (blockReason) {
        setError(`Gemini declined to generate content (reason: ${blockReason}). Try rephrasing your topic.`);
        return;
      }

      let geminiText = response.data.candidates?.[0]?.content?.parts?.[0]?.text;
      if (!geminiText) {
        setError("Gemini returned an empty response. Try again or rephrase your topic.");
        return;
      }
      geminiText = geminiText.trim();

      // Swap the model's {{LINK}} placeholder for your real affiliate URL —
      // never trust the model to reproduce a long tracking URL correctly itself.
      if (cleanLink) {
        if (geminiText.includes("{{LINK}}")) {
          geminiText = geminiText.replaceAll("{{LINK}}", cleanLink);
        } else {
          // Model forgot the CTA line — append one so the link isn't lost.
          geminiText = `${geminiText}\n\nCheck it out here: ${cleanLink}`;
        }
      }
      setBlog(geminiText);
    } catch (e) {
      const status = e.response?.status;
      if (status === 400) {
        setError("Gemini rejected the request — your API key is likely invalid or malformed.");
      } else if (status === 403) {
        setError("Access denied — check that your API key has Gemini API access enabled.");
      } else if (status === 429) {
        setError("Rate limit or quota exceeded for this API key. Wait a moment and try again.");
      } else if (!e.response) {
        setError("Network error — check your internet connection and try again.");
      } else {
        setError(`Failed to fetch from Gemini (HTTP ${status}). Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  }

  // Strip any stray Markdown the model might slip in (bold, bullets, heading
  // marks) so what gets copied/downloaded is 100% plain text — LinkedIn does
  // not render Markdown, so leftover "**"/"##"/"- " would show up literally
  // in the published post.
  function stripMarkdown(text) {
    return text
      .replace(/^#{1,6}\s+/gm, "")
      .replace(/\*\*(.+?)\*\*/g, "$1")
      .replace(/^[-*]\s+/gm, "");
  }

  function getPlainTextPost(text) {
    const { title, bodyText } = splitTitleAndBody(text);
    const cleanBody = stripMarkdown(bodyText).trim();
    return title ? `${title}\n\n${cleanBody}` : cleanBody;
  }

  function handleCopy() {
    navigator.clipboard.writeText(getPlainTextPost(blog));
    setCopied(true);
  }

  function handleDownload() {
    const blobFile = new Blob([getPlainTextPost(blog)], { type: "text/plain" });
    const url = URL.createObjectURL(blobFile);
    const a = document.createElement("a");
    const safeName = (phenomenon || "eureka-story").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 60);
    a.href = url;
    a.download = `${safeName}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // Pull the "# " title line out separately so it gets its own hero
  // treatment; everything else is the flowing body.
  function splitTitleAndBody(text) {
    const lines = text.split("\n");
    let title = null;
    const bodyLines = [];
    let titleTaken = false;
    for (const line of lines) {
      if (!titleTaken && line.trim().startsWith("# ")) {
        title = line.trim().slice(2).trim();
        titleTaken = true;
      } else {
        bodyLines.push(line);
      }
    }
    return { title, bodyText: bodyLines.join("\n") };
  }

  function renderInline(text, keyPrefix) {
    // Tolerant of stray **bold** if the model slips — renders it, but the
    // copy/download path always strips it since LinkedIn can't display it.
    const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>;
      }
      return <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>;
    });
  }

  function renderBlog(text) {
    const { title, bodyText } = splitTitleAndBody(text);
    const bodyLines = bodyText.split("\n");

    // Group into blocks separated by blank lines — plain paragraphs
    const blocks = [];
    let current = [];
    for (const line of bodyLines) {
      if (line.trim() === "") {
        if (current.length) blocks.push(current);
        current = [];
      } else {
        current.push(line);
      }
    }
    if (current.length) blocks.push(current);

    const rendered = blocks.map((block, i) => {
      const paragraphText = block.map(l => l.trim()).join(" ");
      return (
        <Typography key={i} variant="body1" sx={{ mb: 2 }}>
          {renderInline(paragraphText, i)}
        </Typography>
      );
    });

    const plainBody = stripMarkdown(bodyText).trim();
    const wordCount = plainBody.split(/\s+/).filter(Boolean).length;
    const charCount = plainBody.length;

    return { title, rendered, wordCount, charCount };
  }

  // --- RENDER FLOW ---
  // 1. Welcome + instructions
  if (!isConnected) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 6, p: 4, bgcolor: "white", borderRadius: 3 }}>
          <Typography variant="h3" align="center" gutterBottom>
            Eureka Journeys
          </Typography>
          <Typography variant="h5" sx={{ mb: 3 }}>
            Discover the real, messy, inspiring human stories behind scientific concepts!
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            <strong>How to use this app:</strong>
            <ul>
              <li>Enter your <b>Gemini 2.5 Flash API Key</b> and click Connect.</li>
              <li>Next, type a phenomenon or concept, and its subject area (e.g., “gravity”, “physics”).</li>
              <li>Click Submit to get a compelling, structured blog post generated for you!</li>
            </ul>
          </Typography>
          <TextField
            label="Gemini API Key"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            type="password"
            onKeyDown={e => e.key === "Enter" && handleConnect()}
          />
          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
          <Button onClick={handleConnect} variant="contained" fullWidth>
            Connect
          </Button>
        </Box>
      </Container>
    );
  }

  const { title, rendered, wordCount, charCount } = blog
    ? renderBlog(blog)
    : { title: null, rendered: null, wordCount: 0, charCount: 0 };
  const overLimit = wordCount > 220;

  // 2. Show concept/subject form and blog output
  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, p: 3, bgcolor: "white", borderRadius: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4">
            Generate a LinkedIn Eureka Story
          </Typography>
          <Tooltip title="Disconnect and use a different API key">
            <Button onClick={handleDisconnect} size="small" color="inherit">
              🔓 Change Key
            </Button>
          </Tooltip>
        </Box>
        <TextField
          label="Phenomenon or Concept"
          placeholder="e.g. Gravity, Electromagnetic Induction, Photoelectric Effect"
          fullWidth
          sx={{ my: 1 }}
          value={phenomenon}
          onChange={e => setPhenomenon(e.target.value)}
          disabled={loading}
        />
        <TextField
          label="Subject"
          placeholder="e.g. Physics"
          fullWidth
          sx={{ my: 1 }}
          value={subject}
          onChange={e => setSubject(e.target.value)}
          disabled={loading}
        />

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2, mb: 0.5 }}>
          Optional — turn this into a product post by linking your Amazon affiliate page
        </Typography>
        <TextField
          label="Product Name (optional)"
          placeholder="e.g. Ergonomic Office Chair"
          fullWidth
          sx={{ my: 1 }}
          value={affiliateProductName}
          onChange={e => setAffiliateProductName(e.target.value)}
          disabled={loading}
        />
        <TextField
          label="Affiliate Link (optional)"
          placeholder="https://www.amazon.in/dp/XXXXXXXXXX/?tag=trust034-21"
          fullWidth
          sx={{ my: 1 }}
          value={affiliateLink}
          onChange={e => setAffiliateLink(e.target.value)}
          disabled={loading}
        />

        {error && <Typography color="error" sx={{ my: 1 }}>{error}</Typography>}
        <Button variant="contained" onClick={handleSubmit} disabled={loading} sx={{ mr: 2, mt: 1 }}>
          {loading ? "Writing your Eureka story..." : "Generate Story"}
        </Button>
        {loading && <CircularProgress size={24} sx={{ ml: 1 }} />}
      </Box>

      {blog && (
        <Paper sx={{ mt: 5, p: 4, background: "#f7f8fa" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "primary.main", lineHeight: 1.3 }}>
              {title || "Your Eureka Story"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexShrink: 0, ml: 2 }}>
              <Tooltip title="Copy as plain text (LinkedIn-ready)">
                <IconButton onClick={handleCopy}>📋</IconButton>
              </Tooltip>
              <Tooltip title="Download as .txt">
                <IconButton onClick={handleDownload}>⬇️</IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Chip
              size="small"
              label={`${wordCount} words · ${charCount} characters`}
              color={overLimit ? "warning" : "success"}
              variant="outlined"
            />
            {overLimit && (
              <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
                Over the 220-word target — trim before posting.
              </Typography>
            )}
          </Box>
          <Box sx={{ lineHeight: 1.7, mt: 2 }}>
            {rendered}
          </Box>
        </Paper>
      )}

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Copied to clipboard"
      />
    </Container>
  );
}
